import { useTranslation } from 'react-i18next';
import { useNotification } from '@topcon/topcon-utils';

import { CombinedUser, Status } from '../types';
import ConfirmDeletionModal from '../../../components/Modal/ConfirmDeletionModal';
import { useDeleteInviteToJoin, useDeleteUser } from '../userManagement.mutations';
import { useErrorDrawer } from '../../../providers/ErrorDrawerContext/error-drawer.context';

interface Props {
  selectedUsers: CombinedUser[];
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
}

function UserRemoveModal({ selectedUsers, open, onClose }: Props) {
  const { t } = useTranslation('users');
  const { mutateAsync: deleteUser } = useDeleteUser();
  const { mutateAsync: deleteInviteToJoin } = useDeleteInviteToJoin();
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();

  const handleUserRemove = async () => {
    return Promise.all(
      selectedUsers.map(({ id, status, name }) =>
        (status === Status.Invited ? deleteInviteToJoin(id) : deleteUser(id)).catch(
          (error: unknown) => {
            if (showError(error)) {
              return;
            }
            notificationService.addNotification(
              t('userRemoveFailed'),
              t('userRemoveFailedDescription', { name }),
              'warning'
            );
          }
        )
      )
    ).then(() => {
      onClose(true);
    });
  };

  const headers = { text: t('name'), subText: t('email') };
  const data = selectedUsers.map((user) => {
    const { id, name: text, email: subText } = user;
    return { id, text, subText };
  });

  return (
    <ConfirmDeletionModal
      title={t('removeUserConfirmation')}
      description={t('removeUserModal.infoText', { count: selectedUsers.length })}
      headers={headers}
      data={data}
      primaryButtonText={t('removeUserModal.removeUserButton', { count: selectedUsers.length })}
      iconName="account_circle"
      iconColor="icon.muted"
      open={open}
      onSubmit={handleUserRemove}
      onClose={onClose}
    />
  );
}

export default UserRemoveModal;
